import React, {Component} from 'react';
import classNames from 'classnames';
import {FormattedMessage, injectIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {connect} from 'react-redux';
import {constants} from '@neosvoc/nv-platform-core-common';
import get from 'lodash/get';
import messages from '../../../res/translations/messages';
import SurveyDeleteButton from './SurveyDeleteButton';
import CawiLink from './CawiLink';
import SurveyListLink from './SurveyListLink';
import Icon from '../Icon';
import IconSeparator from '../IconSeparator';
import EditLink from './EditLink';
import SurveyCloneButton from './SurveyCloneButton';
import TooltipContainer from '../TooltipContainer';
import SurveyDeleteInstancesButton from './SurveyDeleteInstancesButton';
import PublishedBadge from './PublishedBadge';
import {userHasPermission} from '../../utils/auth';
import SurveyDownloadButton from './SurveyDownloadButton';

class SurveyTableRow extends Component {
    constructor(props) {
        super(props);

        this.handleDelete = this.handleDelete.bind(this);
        this.handleDeleteInstances = this.handleDeleteInstances.bind(this);
        this.handleExport = this.handleExport.bind(this);
    }

    handleDelete() {
        this.props.onDelete(this.props.item.id);
    }

    handleDeleteInstances() {
        this.props.onDeleteInstances(this.props.item.id);
    }

    handleExport(params) {
        this.props.onExport(this.props.item.id, params);
    }

    render() {
        const {item, intl, roles, exporting} = this.props;
        const canDashboard = userHasPermission(roles, constants.PERMISSIONS.DASHBOARD_VIEW);
        const canExport = userHasPermission(roles, constants.PERMISSIONS.INSTANCES_EXPORT);
        const canEditSurvey = userHasPermission(roles, constants.PERMISSIONS.SURVEY_EDIT);
        const canShareSurvey = userHasPermission(roles, constants.PERMISSIONS.SURVEY_SHARE);
        const canTrackers = userHasPermission(roles, constants.PERMISSIONS.TRACKERS);
        return (
            <div className="survey-table-row row">
                <div className="col">
                    <h2>
                        {item.name}
                        <PublishedBadge value={item.published} />
                    </h2>
                    <div className="survey-dates">
                        <span className="created-at">
                            <FormattedMessage {...messages.surveys.list.createdAt} values={{date: intl.formatDate(item.createdAt)}} />
                        </span>
                        <span className="updated-at ml-1">
                            <FormattedMessage {...messages.surveys.list.updatedAt} values={{date: intl.formatDate(item.updatedAt)}} />
                        </span>
                    </div>
                    <div className="toolbar">
                        <TooltipContainer tooltip={messages.surveys.tooltips.edit}>
                            <EditLink id={item.id} disabled={!canEditSurvey || !item.editable}>
                                <Icon icon="edit" />
                            </EditLink>
                        </TooltipContainer>
                        <TooltipContainer tooltip={messages.surveys.tooltips.clone}>
                            <SurveyCloneButton item={item} disabled={!canEditSurvey || !item.editable}>
                                <FontAwesomeIcon icon={['far', 'copy']} fixedWidth={true} />
                            </SurveyCloneButton>
                        </TooltipContainer>
                        <TooltipContainer tooltip={messages.surveys.tooltips.share}>
                            <Link to={canShareSurvey ? `/surveys/${item.id}/share` : ''} className={classNames({disabled: !canShareSurvey})}>
                                <FontAwesomeIcon icon="qrcode" fixedWidth={true} />
                            </Link>
                        </TooltipContainer>
                        <TooltipContainer tooltip={messages.surveys.tooltips.preview}>
                            <CawiLink id={item.id} disabled={!canShareSurvey || !item.channels.includes(constants.CHANNEL_TYPES.CAWI)} />
                        </TooltipContainer>
                        <TooltipContainer tooltip={messages.surveys.tooltips.download}>
                            <SurveyDownloadButton item={item}
                                disabled={(!(item.stats.started) || !canExport || exporting)}
                                surveyCompleted={item.stats.completed} surveyStarted={item.stats.started}
                                onExport={this.handleExport} showEmptyWarning={(!item.stats.completed && !item.stats.started)}>
                                {exporting ?
                                    <span className = "fa-layers fa-fw" >
                                        <FontAwesomeIcon icon="file-download" fixedWidth={true} />
                                        <FontAwesomeIcon icon="circle-notch" spin color="#6c757d" fixedWidth={true} />
                                    </span>:
                                    <FontAwesomeIcon icon="file-download" fixedWidth={true} />
                                }
                            </SurveyDownloadButton>
                        </TooltipContainer>
                        <TooltipContainer tooltip={messages.surveys.tooltips.export}>
                            <SurveyListLink url={`/surveys/${item.id}/export`} disabled={(!(item.stats.questions > 0) || !canExport)}>
                                <Icon icon="export" />
                            </SurveyListLink>
                        </TooltipContainer>
                        <TooltipContainer tooltip={messages.surveys.tooltips.viewDashboard}>
                            <SurveyListLink url={`/dashboard/${item.id}`} disabled={(!(item.stats.started) || !canDashboard)}>
                                <Icon icon="istogram" />
                            </SurveyListLink>
                        </TooltipContainer>
                        <IconSeparator />
                        <TooltipContainer component={Link} tooltip={messages.surveys.tooltips.viewTrackers}
                            to={canTrackers ? `/surveys/${item.id}/trackers` : '/surveys'}
                            className={classNames('mx-1', {disabled: !canTrackers})}>
                            <FontAwesomeIcon icon="users-cog" />
                        </TooltipContainer>
                        <IconSeparator />
                        <TooltipContainer tooltip={messages.surveys.tooltips.deleteInstances}>
                            <SurveyDeleteInstancesButton onClick={this.handleDeleteInstances} item={item} disabled={!item.stats.started || !canEditSurvey}
                                className="mx-1">
                                <FontAwesomeIcon icon="backspace" />
                            </SurveyDeleteInstancesButton>
                        </TooltipContainer>
                        <IconSeparator />
                        <TooltipContainer tooltip={messages.surveys.tooltips.delete}>
                            <SurveyDeleteButton onDelete={this.handleDelete} item={item} disabled={!canEditSurvey}>
                                <Icon icon="trash" />
                            </SurveyDeleteButton>
                        </TooltipContainer>
                        {item.consoleButtons.length > 0 && (
                            <span className="console-buttons">{item.consoleButtons.map((b, i) => (
                                <a target="_blank" rel="noopener noreferrer" href={b.url} key={i}>{b.label}</a>
                            ))}</span>
                        )}
                    </div>
                </div>
                <div className="d-none d-sm-flex col-2 stats">
                    <strong>{item.stats.started}</strong>
                    <FormattedMessage {...messages.surveys.list.started} />
                </div>
                <div className="col-sm-2 col-4 stats">
                    <strong>{item.stats.completed}</strong>
                    <FormattedMessage {...messages.surveys.list.completed} />
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    roles: get(state, 'auth.user.roles', []),
    exporting: ownProps.item? get(state, `exports.items.${ownProps.item.id}.exporting`, false): false,
});

export default connect(mapStateToProps)(injectIntl(SurveyTableRow));
